(() => {
  const REQUEST_TIMEOUT = 15000;
  const DEFAULT_MESSAGES = {
    loading: 'Cargando tipos de PQR.',
    catalogError: 'No se pudieron cargar los tipos de PQR. Intenta de nuevo.',
    submitting: 'Enviando tu PQR.',
    success: 'Tu PQR fue registrado con el radicado {radicado}.',
    validation: 'Revisa los datos e inténtalo de nuevo.',
    unknown: 'No pudimos confirmar tu PQR. Intenta de nuevo sin cambiar los datos.',
  };

  const fields = ['nombre', 'correo', 'telefono', 'comentario', 'tipo', 'actualizacionDatos', 'tratamientoDatos'];

  function message(form, name) {
    return form.dataset[`pqr${name[0].toUpperCase()}${name.slice(1)}`] || DEFAULT_MESSAGES[name];
  }

  function sameOriginUrl(path) {
    const url = new URL(path, window.location.origin);
    if (url.origin !== window.location.origin) throw new Error('PQR endpoint must use the storefront origin');
    return url.href;
  }

  function statusElements(form) {
    return {
      loading: form.querySelector('[data-pqr-status="loading"]'),
      submitting: form.querySelector('[data-pqr-status="submitting"]'),
      error: form.querySelector('[data-pqr-status="error"]'),
      unknown: form.querySelector('[data-pqr-status="unknown"]'),
      success: form.querySelector('[data-pqr-status="success"]'),
    };
  }

  function showStatus(form, type, text, focus = true) {
    const elements = statusElements(form);
    Object.values(elements).forEach((element) => {
      if (element) element.hidden = true;
    });
    const element = elements[type] || elements.error;
    if (!element) return;
    const messageElement = element.querySelector('[data-pqr-message]');
    if (messageElement) messageElement.textContent = text;
    else element.textContent = text;
    element.hidden = false;
    if (focus) {
      if (!element.hasAttribute('tabindex')) element.setAttribute('tabindex', '-1');
      element.focus({ preventScroll: true });
    }
  }

  function responseMessage(body, fallback) {
    if (!body || typeof body !== 'object') return fallback;
    const summary = typeof body.mensaje === 'string' ? body.mensaje.trim() : '';
    const reasons = body.campos && typeof body.campos === 'object'
      ? Object.values(body.campos).filter((value) => typeof value === 'string' && value.trim())
      : [];
    if (reasons.length) return `${summary ? `${summary}: ` : ''}${reasons.join('. ')}.`;
    return summary || fallback;
  }

  async function readJson(response) {
    try {
      return await response.json();
    } catch (_error) {
      return null;
    }
  }

  // ponytail: one-entry accent map; delete it when the API sends accented labels.
  const LABELS = { Peticion: 'Petici\u00f3n' };

  function setOptions(select, catalog) {
    const options = Array.isArray(catalog) ? catalog : catalog && (catalog.tipos || catalog.data);
    if (!Array.isArray(options)) throw new Error('Invalid PQR type catalog');
    select.options.length = 1;
    options.forEach((item) => {
      if (!item || typeof item.valor !== 'string' || typeof item.etiqueta !== 'string') return;
      const option = document.createElement('option');
      option.value = item.valor;
      option.textContent = LABELS[item.valor] || item.etiqueta;
      select.append(option);
    });
  }

  async function loadCatalog(form, select, submit) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);
    showStatus(form, 'loading', message(form, 'loading'), false);
    try {
      const response = await fetch(sameOriginUrl(form.dataset.pqrTypesEndpoint || '/apps/pqr/tipos'), {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`Catalog request failed: ${response.status}`);
      setOptions(select, await readJson(response));
      select.disabled = false;
      submit.disabled = false;
      const loading = statusElements(form).loading;
      if (loading) loading.hidden = true;
      form.__pqrCatalogLoaded = true;
    } catch (_error) {
      showStatus(form, 'error', message(form, 'catalogError'));
    } finally {
      clearTimeout(timeout);
    }
  }

  function formPayload(form) {
    const data = new FormData(form);
    return fields.reduce((payload, field) => {
      const element = form.elements.namedItem(field);
      payload[field] = element && element.type === 'checkbox' ? Boolean(element.checked) : String(data.get(field) || '').trim();
      return payload;
    }, {});
  }

  async function submitPqr(form, submit) {
    if (form.__pqrSubmitting) return;
    form.__pqrSubmitting = true;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);
    showStatus(form, 'submitting', message(form, 'submitting'), false);
    submit.disabled = true;
    try {
      const response = await fetch(sameOriginUrl(form.dataset.pqrEndpoint || '/apps/pqr/pqr'), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify(formPayload(form)),
        signal: controller.signal,
      });
      const body = await readJson(response);

      if (response.status === 201) {
        const radicado = body && typeof body.radicado === 'string' && body.radicado.trim();
        if (!radicado) {
          showStatus(form, 'unknown', message(form, 'unknown'));
          return;
        }
        form.reset();
        const success = message(form, 'success').replace('{radicado}', radicado);
        showStatus(form, 'success', success);
        return;
      }

      if (response.status === 422) {
        showStatus(form, 'error', responseMessage(body, message(form, 'validation')));
        return;
      }

      showStatus(form, 'unknown', message(form, 'unknown'));
    } catch (_error) {
      showStatus(form, 'unknown', message(form, 'unknown'));
    } finally {
      clearTimeout(timeout);
      form.__pqrSubmitting = false;
      submit.disabled = !form.__pqrCatalogLoaded;
    }
  }

  function initialize(form) {
    if (form.dataset.pqrInitialized) return;
    const select = form.elements.namedItem('tipo') || form.querySelector('#ContactForm-pqr-type');
    const submit = form.querySelector('[type="submit"]');
    if (!select || !submit) return;
    form.dataset.pqrInitialized = 'true';
    form.__pqrCatalogLoaded = false;
    select.disabled = true;
    submit.disabled = true;
    loadCatalog(form, select, submit);
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      if (!form.__pqrCatalogLoaded || !form.reportValidity()) return;
      submitPqr(form, submit);
    });
  }

  function initializeAll(root = document) {
    root.querySelectorAll('form[data-pqr-endpoint], form#ContactForm').forEach(initialize);
  }

  initializeAll();
  document.addEventListener('DOMContentLoaded', () => initializeAll());
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
